import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import Navigation from "./Navigation.jsx";
import { ReactComponent as Message } from "../assets/message.svg";
import { ReactComponent as Logo } from "../assets/logo.svg";
import { ReactComponent as LogoGrey } from "../assets/logo_grey.svg";
// import { ReactComponent as LogoMobile } from "../assets/logo_mobile.svg"; // Unused for now
import { ReactComponent as UIDO } from "../assets/uido-text.svg";
import { ReactComponent as UIDOLogoFixed } from "../assets/uido_logo_fixed.svg";
import { ReactComponent as MobileMenu } from "../assets/mobile_menu.svg";
import MobileMenuComponent from "./MobileMenuComponent.jsx";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { switchLanguageWithFade } from "../utils/languageSwitch";
import { isHomePath } from "../utils/homePath";
// Removed PopupModal import - using badge widget instead
import ButtonWithIcon from "./ButtonWithIcon";

const Header = () => {
  const navDebug = (...args) => {
    if (typeof window !== "undefined" && window.__NAV_DEBUG__ === false) return;
    console.log("[NavDebug]", ...args);
  };

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMenuAnimating, setIsMenuAnimating] = useState(false);
  const [isFixed, setIsFixed] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const [showRegularHeader, setShowRegularHeader] = useState(false);
  const [isDesktopViewport, setIsDesktopViewport] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 768 : true
  );
  const lastYRef = useRef(0);
  const rafLockRef = useRef(false);
  const suppressRevealRef = useRef(false);
  // Removed isCalendlyOpen state - using badge widget instead
  const [isConsultationOpen, setConsultationOpen] = useState(false);
  const [isConsultationClosing, setConsultationClosing] = useState(false);
  const [isOverlayVisible, setOverlayVisible] = useState(false);
  const [isLanguageSwitching, setIsLanguageSwitching] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const floatingHeaderRef = useRef(null);
  const engageThresholdRef = useRef(80);
  const compactMobileThresholdRef = useRef(140);
  const mobileMenuBodyLockRef = useRef(null);

  const toggleMenu = () => {
    if (isMenuAnimating && !isMenuOpen) {
      navDebug("toggleMenu:blocked-during-closing");
      return;
    }

    navDebug("toggleMenu:start", {
      isMenuOpen,
      isMenuAnimating,
      isMobileViewport: typeof window !== "undefined" ? window.innerWidth < 768 : null,
      scrollY: typeof window !== "undefined" ? window.scrollY : null,
    });
    if (isMenuOpen) {
      // Restore page scroll position when closing mobile menu.
      const lock = mobileMenuBodyLockRef.current;
      if (lock) {
        document.body.style.overflow = lock.overflow || "";
        document.body.style.position = lock.position || "";
        document.body.style.width = lock.width || "";
        document.body.style.top = lock.top || "";
        navDebug("toggleMenu:close:restore-scroll", { restoredScrollY: window.scrollY || 0 });
      }
      // Closing menu
      setIsMenuOpen(false);
      setIsMenuAnimating(true);
      setTimeout(() => {
        setIsMenuAnimating(false);
        mobileMenuBodyLockRef.current = null;
        navDebug("toggleMenu:close:state-updated", { isMenuOpenNext: false, isMenuAnimatingNext: false });
      }, 420); // Match transition duration
    } else {
      // Lock body while mobile menu is open to prevent jump-to-top.
      if (window.innerWidth < 768 && !mobileMenuBodyLockRef.current) {
        const body = document.body;
        mobileMenuBodyLockRef.current = {
          overflow: body.style.overflow,
          position: body.style.position,
          width: body.style.width,
          top: body.style.top,
        };
        body.style.overflow = "hidden";
        navDebug("toggleMenu:open:lock-scroll", { lockScrollY: window.scrollY || 0 });
      }
      // Opening menu: render hidden state first, then trigger transition to open.
      setIsMenuAnimating(true);
      setIsMenuOpen(false);
      requestAnimationFrame(() => {
        setIsMenuOpen(true);
      });
      setTimeout(() => {
        setIsMenuAnimating(false);
        navDebug("toggleMenu:open:state-updated", { isMenuOpenNext: true, isMenuAnimatingNext: false });
      }, 420);
    }
  };

  useEffect(() => {
    const detectModalState = () => {
      const hasModal = Boolean(
        document.querySelector(
          '[aria-modal="true"], .project-modal-overlay, .project-modal, .project-detail-modal, .project-detail-modal-overlay, .faq-modal, .faq-modal-overlay, .choice-modal, .choice-modal-overlay, .cookie-modal'
        )
      );
      const nextIsModalOpen = hasModal;
      setIsModalOpen(nextIsModalOpen);
    };

    detectModalState();
    const observer = new MutationObserver(detectModalState);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "aria-modal"],
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isModalOpen) return;
    // If modal opens while mobile menu is open, cleanup menu lock.
    const lock = mobileMenuBodyLockRef.current;
    if (lock) {
      document.body.style.overflow = lock.overflow || "";
      document.body.style.position = lock.position || "";
      document.body.style.width = lock.width || "";
      document.body.style.top = lock.top || "";
      mobileMenuBodyLockRef.current = null;
    }
    setIsMenuOpen(false);
    setIsMenuAnimating(false);
    setShowRegularHeader(false);
    navDebug("modal-open:force-close-menu");
  }, [isModalOpen]);

  useEffect(() => {
    navDebug("menu-visibility", {
      isMenuOpen,
      isMenuAnimating,
      isModalOpen,
      shouldRenderHeader: !isModalOpen && !isMenuOpen && !isMenuAnimating,
      shouldRenderMobileOverlay: !isModalOpen && (isMenuOpen || isMenuAnimating),
    });
  }, [isMenuOpen, isMenuAnimating, isModalOpen]);

  useEffect(() => () => {
    const lock = mobileMenuBodyLockRef.current;
    if (!lock) return;
    document.body.style.overflow = lock.overflow || "";
    document.body.style.position = lock.position || "";
    document.body.style.width = lock.width || "";
    document.body.style.top = lock.top || "";
    mobileMenuBodyLockRef.current = null;
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 768px)");
    const recalculateEngageThreshold = () => {
      const heroElement =
        document.querySelector(".banner") ||
        document.querySelector(".hero") ||
        document.querySelector(".checklist-hero") ||
        document.querySelector(".policy-hero");

      if (!heroElement) {
        engageThresholdRef.current = 80;
        compactMobileThresholdRef.current = 140;
        return;
      }

      const rect = heroElement.getBoundingClientRect();
      const heroBottomByOffset =
        (heroElement.offsetTop || 0) + (heroElement.offsetHeight || 0);
      const heroBottomByRect = (window.scrollY || 0) + rect.bottom;
      const heroBottom = Math.max(heroBottomByOffset, heroBottomByRect);

      engageThresholdRef.current = Math.max(80, Math.round(heroBottom));
      compactMobileThresholdRef.current = engageThresholdRef.current + 120;
    };

    const syncViewport = () => {
      setIsDesktopViewport(mediaQuery.matches);
    };

    syncViewport();
    recalculateEngageThreshold();
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", syncViewport);
    } else if (typeof mediaQuery.addListener === "function") {
      mediaQuery.addListener(syncViewport);
    }
    window.addEventListener("resize", syncViewport);
    window.addEventListener("resize", recalculateEngageThreshold);

    const onSuppressReveal = (e) => {
      const duration = (e && e.detail && e.detail.duration) || 1200;
      suppressRevealRef.current = true;
      setTimeout(() => {
        suppressRevealRef.current = false;
      }, duration);
    };
    window.addEventListener('header:suppressReveal', onSuppressReveal);

    const handleScroll = () => {
      if (rafLockRef.current) return;
      rafLockRef.current = true;
      requestAnimationFrame(() => {
        const current = window.scrollY || 0;
        const prev = lastYRef.current || 0;
        const delta = current - prev;
        const showHideThreshold = 12; // reduce flicker on mobile
        const engageThreshold = engageThresholdRef.current || 80; // activate after hero

        if (current <= engageThreshold) {
          setIsVisible(true);
          setIsFixed(false);
          setIsScrollingDown(false);
        } else {
          // Once we cross the threshold, keep sticky behavior and switch
          // between compact/full header based on scroll direction.
          setIsFixed(true);

          if (delta <= -showHideThreshold) {
            // scrolling up
            if (!suppressRevealRef.current) {
              setIsVisible(true);
            }
            setIsScrollingDown(false);
          } else if (delta >= showHideThreshold) {
            // scrolling down
            setIsVisible(true); // Keep visible but show minimal nav
            const isMobile = window.innerWidth < 768;
            const shouldShowCompactMenu =
              !isMobile || current > compactMobileThresholdRef.current;
            setIsScrollingDown(shouldShowCompactMenu);
          } else {
            // Ignore tiny trackpad deltas; preserve current compact/full state.
          }
        }

        lastYRef.current = current;
        rafLockRef.current = false;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      if (typeof mediaQuery.removeEventListener === "function") {
        mediaQuery.removeEventListener("change", syncViewport);
      } else if (typeof mediaQuery.removeListener === "function") {
        mediaQuery.removeListener(syncViewport);
      }
      window.removeEventListener("resize", syncViewport);
      window.removeEventListener("resize", recalculateEngageThreshold);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener('header:suppressReveal', onSuppressReveal);
    };
  }, []);

  // const buttonOnClickPrimary = () => {}; // Unused for now

  const calendlyClick = () => {
    // Open Calendly popup widget
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: 'https://calendly.com/uidopl/bezplatna-konsultacja?primary_color=cc4429'
      });
    } else {
      // Fallback: open in new tab if Calendly script not loaded
      window.open('https://calendly.com/uidopl/bezplatna-konsultacja?primary_color=cc4429', '_blank', 'noopener,noreferrer');
    }
  };
  const navigate = useNavigate();
  const location = useLocation();
  const isHomeRoute = isHomePath(location.pathname);
  const goHome = () => {
    // If already on home page, scroll to top like "Start" button does
    if (isHomeRoute) {
      try { window.dispatchEvent(new CustomEvent('overlay:show', { detail: { duration: 900 } })); } catch(_) {}
      const element = document.querySelector('.home');
      if (element) {
        const rect = element.getBoundingClientRect();
        const pageTop = window.pageYOffset || document.documentElement.scrollTop || 0;
        const top = rect.top + pageTop;
        window.scrollTo({ top, behavior: 'smooth' });
      } else {
        // Fallback: just scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }
    // If on different page, navigate to home
    try {
      window.dispatchEvent(new CustomEvent('header:suppressReveal', { detail: { duration: 600 } }));
    } catch (_) {}
    navigate("/");
  };

  const goToForm = () => {
    const formId = "#form";

    if (isHomeRoute) {
      const formElement = document.querySelector(formId);
      if (formElement) {
        const formPosition =
          formElement.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: formPosition + 200, // Offset 200px below
          behavior: "smooth",
        });
        setOverlayVisible(true);
        setTimeout(() => setOverlayVisible(false), 900);
      }
    } else {
      navigate("/", { replace: true });
      setTimeout(() => {
        const formElement = document.querySelector(formId);
        if (formElement) {
          const formPosition =
            formElement.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({
            top: formPosition + 200, // Offset 200px below
            behavior: "smooth",
          });
          setOverlayVisible(true);
          setTimeout(() => setOverlayVisible(false), 900);
        }
      }, 100);
    }
  };

  const { t, i18n } = useTranslation();

  // Listen for global event to open Calendly popup (used by other components)
  useEffect(() => {
    const onCalendlyOpen = () => {
      calendlyClick();
    };
    window.addEventListener('calendly:open', onCalendlyOpen);
    return () => window.removeEventListener('calendly:open', onCalendlyOpen);
  }, []);

  // Consultation dropdown handlers
  // const toggleConsultation = () => { // Unused for now
  //   setConsultationOpen((prev) => {
  //     if (prev) {
  //       // going from open -> close, trigger closing animation class
  //       setConsultationClosing(true);
  //       setTimeout(() => setConsultationClosing(false), 450);
  //       return false;
  //     }
  //     // open immediately
  //     setConsultationClosing(false);
  //     return true;
  //   });
  // };

  const closeConsultation = () => setConsultationOpen(false);

  useEffect(() => {
    const onClickOutside = (e) => {
      const wrappers = Array.from(document.querySelectorAll('.consultation-wrapper'));
      const clickedInsideAny = wrappers.some((w) => w.contains(e.target));
      if (!clickedInsideAny) {
        if (isConsultationOpen) {
          setConsultationOpen(false);
          setConsultationClosing(true);
          setTimeout(() => setConsultationClosing(false), 450);
        }
      }
    };
    if (isConsultationOpen) {
      document.addEventListener('click', onClickOutside);
    }
    return () => document.removeEventListener('click', onClickOutside);
  }, [isConsultationOpen]);

  const handleOptionConsultation = () => {
    closeConsultation();
    calendlyClick();
  };
  const handleOptionForm = () => {
    closeConsultation();
    goToForm();
  };
  const handleOptionPhone = () => {
    closeConsultation();
    const footer = document.querySelector('[data-section="contact-footer"]');
    setOverlayVisible(true);
    if (footer) footer.scrollIntoView({ behavior: 'smooth' });
    setTimeout(() => setOverlayVisible(false), 900);
  };
  const handleOptionEmail = () => {
    closeConsultation();
    const footer = document.querySelector('[data-section="contact-footer"]');
    setOverlayVisible(true);
    if (footer) footer.scrollIntoView({ behavior: 'smooth' });
    setTimeout(() => setOverlayVisible(false), 900);
  };

  const changeLanguage = async (lng) => {
    if (isLanguageSwitching) return;

    setIsLanguageSwitching(true);
    try {
      await switchLanguageWithFade({
        lng,
        i18n,
        location,
        navigate,
      });
    } finally {
      setIsLanguageSwitching(false);
    }
  };

  const handleMinimalNavClick = () => {
    navDebug("minimal-nav:click", { isFixed, isScrollingDown, showRegularHeader });
    setShowRegularHeader(true);
    // Auto-hide after 3 seconds
    setTimeout(() => {
      setShowRegularHeader(false);
    }, 3000);
  };

  // Minimal navigation for scroll down state
  const MinimalNavContent = () => (
    <div className="minimal-nav" onClick={handleMinimalNavClick}>
      <div className="minimal-nav-content">
        <div
          className="logo minimal"
          onClick={(e) => {
            e.stopPropagation();
            if (isDesktopViewport) {
              goHome();
              return;
            }
            handleMinimalNavClick();
          }}
          role="button"
          tabIndex={0}
          aria-label="Przejdź do strony głównej"
          onKeyDown={(e) => {
            if (e.key !== "Enter" && e.key !== " ") return;
            e.preventDefault();
            if (isDesktopViewport) {
              goHome();
              return;
            }
            handleMinimalNavClick();
          }}
        >
          <div className="uido-swap minimal" aria-hidden="true">
            <LogoGrey className="uido grey" />
          </div>
          <div className="logo-hover-description">
            <div className="description">
              <span className="top-text">REDESIGN</span>
              <br />
              <span className="bottom-text">SELEKTYWNY</span>
            </div>
          </div>
        </div>
        <div className="minimal-nav-controls mobile">
          <div
            className="message-icon"
            onClick={(e) => {
              e.stopPropagation();
              calendlyClick();
            }}
          >
            <Message />
          </div>
          <div
            className="menu-icon"
            onClick={(e) => {
              e.stopPropagation();
              toggleMenu();
            }}
          >
            <MobileMenu />
          </div>
        </div>
        <ButtonWithIcon 
          className="minimal-btn" 
          onClick={(e) => { e.stopPropagation(); calendlyClick(); }}
          iconLeft={Message}
        >
          {t("cta.free")}
        </ButtonWithIcon>
      </div>
    </div>
  );

  // Header content component to avoid code duplication
  const HeaderContent = ({ isFixedHeader = false }) => (
    <>
      {isFixedHeader ? (
        <>
          <div className="header-left desktop">
            <div
              className="logo compact"
              onClick={goHome}
              role="button"
              tabIndex={0}
              aria-label="Przejdź do strony głównej"
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') goHome(); }}
            >
              <div className="logo-icon">
                <Logo />
              </div>
              <svg width="106" height="29" viewBox="0 0 106 29" fill="none" xmlns="http://www.w3.org/2000/svg" className="uido-text">
                <g id="Group">
                  <path id="Vector" d="M19.2896 15.8925C19.2896 17.0764 19.0696 18.1051 18.6275 18.9746C18.1854 19.8463 17.5254 20.5125 16.6412 20.9777C15.7591 21.4428 14.6549 21.6733 13.3328 21.6733C12.0107 21.6733 10.9338 21.4407 10.0412 20.9777C9.14653 20.5125 8.47396 19.8463 8.02139 18.9746C7.56881 18.103 7.34253 17.0764 7.34253 15.8925V1.4375H0.132812V16.2404C0.132812 18.6331 0.683862 20.7158 1.78805 22.4925C2.89015 24.2693 4.43434 25.6501 6.42062 26.6369C8.40691 27.6238 10.7096 28.1183 13.3349 28.1183C15.9602 28.1183 18.2922 27.6238 20.266 26.6369C22.2397 25.6501 23.7776 24.2693 24.8818 22.4925C25.9839 20.7158 26.537 18.6331 26.537 16.2404V1.4375H19.2917V15.8925H19.2896Z" fill="#EFE8DD"></path>
                  <path id="Vector_2" d="M38.2722 1.4375H31.0625V27.5609H38.2722V1.4375Z" fill="#EFE8DD"></path>
                  <path id="Vector_3" d="M64.3083 3.05712C61.9742 1.97807 59.2629 1.4375 56.1745 1.4375H43.1484V27.5609H56.1745C59.2629 27.5609 61.9742 27.0204 64.3083 25.9413C66.6423 24.8623 68.4526 23.3411 69.7412 21.3779C71.0298 19.4167 71.6751 17.1225 71.6751 14.4992C71.6751 11.876 71.0298 9.58169 69.7412 7.62055C68.4526 5.65941 66.6403 4.13826 64.3083 3.05712ZM63.4555 18.0171C62.829 19.0165 61.9406 19.7876 60.7903 20.3324C59.6401 20.8771 58.2886 21.1516 56.7319 21.1516H50.3225V7.84683H56.7319C58.2865 7.84683 59.6401 8.11921 60.7903 8.66607C61.9385 9.21083 62.8269 9.97769 63.4555 10.9645C64.082 11.9514 64.3962 13.131 64.3962 14.4992C64.3962 15.8674 64.082 17.0198 63.4555 18.0171Z" fill="#EFE8DD"></path>
                  <path id="Vector_4" d="M103.334 7.35934C102.033 5.31648 100.223 3.72619 97.901 2.58848C95.5795 1.45076 92.862 0.880859 89.7506 0.880859C86.6392 0.880859 83.951 1.45076 81.6169 2.58848C79.2828 3.72619 77.4725 5.31648 76.1839 7.35934C74.8953 9.40219 74.25 11.7824 74.25 14.4999C74.25 17.2174 74.8953 19.5976 76.1839 21.6405C77.4725 23.6833 79.2849 25.2757 81.6169 26.4113C83.951 27.549 86.6622 28.119 89.7506 28.119C92.839 28.119 95.5795 27.5511 97.901 26.4113C100.223 25.2736 102.035 23.6833 103.334 21.6405C104.635 19.5976 105.285 17.2174 105.285 14.4999C105.285 11.7824 104.635 9.40219 103.334 7.35934ZM96.9771 18.3321C96.315 19.4007 95.3679 20.2241 94.138 20.8045C92.906 21.3849 91.4435 21.6761 89.7485 21.6761C88.0534 21.6761 86.6245 21.387 85.3946 20.8045C84.1626 20.2241 83.2113 19.4007 82.5388 18.3321C81.8641 17.2635 81.5289 15.9875 81.5289 14.4999C81.5289 13.0123 81.8662 11.7363 82.5388 10.6677C83.2113 9.59914 84.1647 8.77572 85.3946 8.19534C86.6245 7.61495 88.0765 7.32372 89.7485 7.32372C91.4205 7.32372 92.906 7.61495 94.138 8.19534C95.3679 8.77572 96.315 9.60124 96.9771 10.6677C97.6391 11.7363 97.9702 13.0123 97.9702 14.4999C97.9702 15.9875 97.6391 17.2635 96.9771 18.3321Z" fill="#EFE8DD"></path>
                </g>
              </svg>
            </div>
            <div className="description">
              <span className="top-text">REDESIGN</span>
              <br />
              SELEKTYWNY
            </div>
          </div>

          <div className="header-left mobile">
            <div className="uido-logo-fixed-mobile">
              <UIDOLogoFixed />
            </div>
            <div className="icons">
              <div className="message-icon" onClick={calendlyClick}>
                <Message />
              </div>
              <div className="menu-icon" onClick={toggleMenu}>
                <MobileMenu />
              </div>
            </div>
          </div>

          <div className="desktop">
            <Navigation />
          </div>

          <div className={`consultation-wrapper${isConsultationOpen ? ' is-open' : ''}${isConsultationClosing ? ' is-closing' : ''}`}>
            <ButtonWithIcon className="desktop" onClick={(e) => { e.stopPropagation(); calendlyClick(); }} iconLeft={Message}>
              {t("cta.free")}
            </ButtonWithIcon>
            <div className="consultation-dropdown" role="menu" aria-label="consultation-options">
              <button type="button" className="consultation-option" onClick={handleOptionConsultation}>
                <span className="option-label">{t("consultation.option.15min")}</span>
              </button>
              <button type="button" className="consultation-option" onClick={handleOptionForm}>
                <span className="option-label">{t("consultation.option.form")}</span>
              </button>
              <button type="button" className="consultation-option" onClick={handleOptionPhone}>
                <span className="option-label">{t("consultation.option.phone")}</span>
              </button>
              <button type="button" className="consultation-option" onClick={handleOptionEmail}>
                <span className="option-label">{t("consultation.option.email")}</span>
              </button>
            </div>
          </div>


            <div className="lang desktop">
              <button
                className={`clickable-button polish ${
                  i18n.language === "pl" ? "active" : ""
                }`}
                onClick={() => changeLanguage("pl")}
                disabled={isLanguageSwitching}
              >
                PL
              </button>
              <button
                className={`clickable-button english ${
                  i18n.language === "en" ? "active" : ""
                }`}
                onClick={() => changeLanguage("en")}
                disabled={isLanguageSwitching}
              >
                EN
              </button>
            </div>
        </>
      ) : (
        <>
          <div className="header-left desktop">
            <div
              className="logo"
              onClick={goHome}
              role="button"
              tabIndex={0}
              aria-label="Przejdź do strony głównej"
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') goHome(); }}
            >
              <div className="logo-icon">
                <Logo />
              </div>
              <UIDO className="uido-text" />
            </div>
            <div className="description">
              <span className="top-text">REDESIGN</span>
              <br />
              SELEKTYWNY
            </div>
          </div>

          <div className="header-left mobile">
            <div className="lang">
              <button
                className={`clickable-button polish ${
                  i18n.language === "pl" ? "active" : ""
                }`}
                onClick={() => changeLanguage("pl")}
                disabled={isLanguageSwitching}
              >
                PL
              </button>
              <button
                className={`clickable-button english ${
                  i18n.language === "en" ? "active" : ""
                }`}
                onClick={() => changeLanguage("en")}
                disabled={isLanguageSwitching}
              >
                EN
              </button>
            </div>
            <div className="icons">
              <div className="message-icon" onClick={calendlyClick}>
                <Message />
              </div>
              <div className="menu-icon" onClick={toggleMenu}>
                <MobileMenu />
              </div>
            </div>
          </div>

          <div className="desktop">
            <Navigation />
          </div>

          <div className={`consultation-wrapper${isConsultationOpen ? ' is-open' : ''}${isConsultationClosing ? ' is-closing' : ''}`}>
            <ButtonWithIcon className="desktop" onClick={(e) => { e.stopPropagation(); calendlyClick(); }} iconLeft={Message}>
              {t("cta.free")}
            </ButtonWithIcon>
            <div className="consultation-dropdown" role="menu" aria-label="consultation-options">
              <button type="button" className="consultation-option" onClick={handleOptionConsultation}>
                <span className="option-label">{t("consultation.option.15min")}</span>
              </button>
              <button type="button" className="consultation-option" onClick={handleOptionForm}>
                <span className="option-label">{t("consultation.option.form")}</span>
              </button>
              <button type="button" className="consultation-option" onClick={handleOptionPhone}>
                <span className="option-label">{t("consultation.option.phone")}</span>
              </button>
              <button type="button" className="consultation-option" onClick={handleOptionEmail}>
                <span className="option-label">{t("consultation.option.email")}</span>
              </button>
            </div>
          </div>


          <div className="lang desktop">
            <button
              className={`clickable-button polish ${
                i18n.language === "pl" ? "active" : ""
              }`}
              onClick={() => changeLanguage("pl")}
              disabled={isLanguageSwitching}
            >
              PL
            </button>
            <button
              className={`clickable-button english ${
                i18n.language === "en" ? "active" : ""
              }`}
              onClick={() => changeLanguage("en")}
              disabled={isLanguageSwitching}
            >
              EN
            </button>
          </div>
        </>
      )}
    </>
  );

  const shouldFloatHeader = isDesktopViewport || isFixed || !isHomeRoute;
  const shouldRenderHeader = !isModalOpen && !isMenuOpen && !isMenuAnimating;

  const headerElement = (
    <header
        ref={floatingHeaderRef}
        className={`header ${shouldFloatHeader ? "fixed" : "normal"} visible ${shouldFloatHeader && isDesktopViewport ? "desktop-fixed-visible" : ""} ${
          !isFixed ? "top-transparent" : ""
        } ${
          isFixed && isScrollingDown && !showRegularHeader ? "scrolling-down" : ""
        }`}
        style={shouldFloatHeader ? {
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          transform: "translateY(0)",
          transition: "transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        } : {
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
        }}
      >
        {isFixed && isScrollingDown && !showRegularHeader ? (
          <MinimalNavContent />
        ) : !isFixed ? (
          <HeaderContent />
        ) : (
          <HeaderContent isFixedHeader />
        )}
      </header>
  );

  return (
    <>
      {/* Always-floating header implementation */}
      {shouldRenderHeader &&
        (typeof document !== "undefined" ? createPortal(headerElement, document.body) : headerElement)}

      {/* Mobile menu - always available regardless of header state */}
      {!isModalOpen && (isMenuOpen || isMenuAnimating) &&
        (typeof document !== "undefined"
          ? createPortal(
              <div className={`header-mobile mobile ${isMenuOpen ? "open" : isMenuAnimating ? "closing" : ""}`}>
                <MobileMenuComponent
                  buttonOnClickPrimary={calendlyClick}
                  toggleMenu={toggleMenu}
                />
              </div>,
              document.body
            )
          : (
              <div className={`header-mobile mobile ${isMenuOpen ? "open" : isMenuAnimating ? "closing" : ""}`}>
                <MobileMenuComponent
                  buttonOnClickPrimary={calendlyClick}
                  toggleMenu={toggleMenu}
                />
              </div>
            ))}

      {/* Calendly badge widget is now loaded in public/index.html */}
      <div className={`page-overlay${isOverlayVisible ? ' is-visible' : ''}`} aria-hidden="true"></div>
    </>
  );
};

export default Header;
